/**
 * The stock family's tab vocabulary: what `?tab=` may name, in the order the
 * sidebar's stock section and the page's own strip list them (nav-groups.ts
 * `STOCK_PAGES` draws both). `analysis` is called **Technical** since V2,
 * because the panel under it is the momentum/bands/volume read and
 * "Analysis" was also what members called the Advanced section.
 */
export const STOCK_TABS = [
  'overview',
  'financials',
  'technical',
  'floorsheet',
  'advanced',
  'events',
  'dividend',
  'share-holding',
  'price-history',
  'broker-holding',
  'agm-history',
  'announcements',
  'compare',
  'reports',
] as const

export type StockTab = (typeof STOCK_TABS)[number]

/**
 * V1 spellings that still have to resolve, because they are in members'
 * history and in links already sent.
 *
 * Without this an old `?tab=analysis` URL would not merely mis-route, it would
 * fall through the enum to `overview` — silently landing someone on a
 * different screen than the one they bookmarked.
 */
const TAB_ALIASES: Record<string, StockTab> = { analysis: 'technical' }

/** Resolves a raw `?tab=` value to a real tab, following aliases. */
export function normaliseStockTab(raw: string | undefined): StockTab {
  const value = raw ?? 'overview'
  const aliased = TAB_ALIASES[value] ?? value
  return STOCK_TABS.some((tab) => tab === aliased) ? (aliased as StockTab) : 'overview'
}

/**
 * The Advanced tab's sub-tabs, in the order the strip draws them: SWOT, Health,
 * Earnings, Value, Charts, Valuation, Peers, Returns, Trading (`stock.pdf`
 * frames 16–20, `swot.pdf`, `valuation.pdf` — every frame draws the same nine).
 *
 * A list rather than a hand-written union, so the strip, the URL parse and the
 * skeleton all read one source, and a sector that lacks a sub-tab can hide it
 * by filtering this list — `normaliseAdvancedTab`'s second argument is what
 * keeps a URL for a hidden one from selecting a chip that is not on the strip.
 * `value` is what `?sub=` carries.
 */
export const ADVANCED_TABS = [
  { value: 'swot', label: 'SWOT' },
  { value: 'health', label: 'Health' },
  { value: 'earnings', label: 'Earnings' },
  { value: 'value', label: 'Value' },
  { value: 'charts', label: 'Charts' },
  { value: 'valuation', label: 'Valuation' },
  { value: 'peers', label: 'Peers' },
  { value: 'returns', label: 'Returns' },
  { value: 'trading', label: 'Trading' },
] as const

export type AdvancedTab = (typeof ADVANCED_TABS)[number]['value']

/**
 * Every sub-tab has a body now (owner ruling C8, 2026-10-29): Value reads the filed figures it
 * can, and Valuation draws its "not available yet" state until mm-be serves one. The route's
 * `?sub=` parse reads this list, so it stays the one place a sub-tab is offered.
 */
export const BUILT_TABS = ADVANCED_TABS
export type BuiltTab = AdvancedTab

/** What `?tab=advanced` opens on, and where an unreadable `?sub=` lands. */
export const DEFAULT_ADVANCED_TAB: AdvancedTab = 'swot'

/** Narrows the `string` a TabBar hands back to a real sub-tab. */
export function isAdvancedTab(value: string): value is AdvancedTab {
  return ADVANCED_TABS.some((tab) => tab.value === value)
}

/**
 * Resolves a raw `?sub=` value to a sub-tab. Anything unknown, empty or absent
 * is SWOT, so `?tab=advanced` and every link sent before the strip existed keep
 * opening where they always did.
 *
 * `tabs` is the set that counts as valid. The route's schema cannot know the
 * company's sector, so it parses against the built sub-tabs (`BUILT_TABS`: a
 * Soon one falls back like an unknown one); the screen that does know can run
 * this again over the sub-tabs the sector has, and a value the sector lacks
 * then falls back the same way. The fallback is SWOT when SWOT is
 * on offer and the first sub-tab otherwise, so the strip never has nothing
 * selected.
 */
export function normaliseAdvancedTab(
  raw: string | undefined,
  tabs: readonly { readonly value: AdvancedTab }[] = ADVANCED_TABS,
): AdvancedTab {
  const match = tabs.find((tab) => tab.value === raw)
  if (match !== undefined) return match.value
  return (
    tabs.find((tab) => tab.value === DEFAULT_ADVANCED_TAB)?.value ??
    tabs[0]?.value ??
    DEFAULT_ADVANCED_TAB
  )
}

/**
 * How many companies a comparison adds to the page's own. Compare and Advanced
 * › Peers both draw "NABIL × EBL × SCB" (`valuation.pdf` frames 7–12: up to
 * three), and `/company/compare/?symbols=` takes two or three.
 */
export const MAX_COMPARE_WITH = 2

/**
 * The route schema's cleaning of `?with=`: trimmed, upper-cased, each symbol
 * once, in the order given.
 *
 * Bounded at one more than a comparison can use, not at `MAX_COMPARE_WITH`: a
 * `validateSearch` sees no path params, so it cannot take the page's own symbol
 * out, and a hand-typed `with=NABIL,GBIME,EBL` on NABIL's page must still end
 * up as GBIME and EBL. Of the first three distinct symbols at most one is the
 * page's own, so two always survive `cleanCompareWith`. The bound also keeps a
 * pasted link from growing the URL.
 */
export function normaliseCompareWith(symbols: readonly string[]): string[] {
  const distinct = new Set<string>()
  for (const raw of symbols) {
    const symbol = raw.trim().toUpperCase()
    if (symbol !== '') distinct.add(symbol)
  }
  return [...distinct].slice(0, MAX_COMPARE_WITH + 1)
}

/**
 * What a comparison may actually add: `normaliseCompareWith`, less the page's
 * own symbol, at most `MAX_COMPARE_WITH`. The screen applies it to what it reads
 * from the URL and to what a panel asks to write, so no panel ever filters its
 * own company out of "the others".
 */
export function cleanCompareWith(symbols: readonly string[], own: string): string[] {
  const ownSymbol = own.trim().toUpperCase()
  return normaliseCompareWith(symbols)
    .filter((symbol) => symbol !== ownSymbol)
    .slice(0, MAX_COMPARE_WITH)
}
